import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cx from "classnames";

import Actions from "src/components/actionbar";
import userRoleService from "src/utils/userRoles.service";
import {
  COACHING_EXPLORE_PATH,
  COACHING_MY_COURSES_PATH,
  COACHING_MY_PODCASTS_PATH,
  getContentManageLink,
  getManageNavigationRootFrom,
  isCoachingOriginPath,
  isPublishedCatalogStatus,
  navigateToManageTarget,
} from "src/pages/coaching/utils/manageNavigation";

import styles from "./contentManageLink.module.scss";

export const canUseContentManageLink = (): boolean =>
  userRoleService.canCreateCourse() ||
  userRoleService.canAccessCoachingReport();

const isReportOnlyCoachingUser = (): boolean =>
  userRoleService.canAccessCoachingReport() &&
  !userRoleService.canCreateCourse();

interface ContentManageLinkProps {
  entityType?: string;
  contentId?: string;
  status?: string;
  from?: string;
  className?: string;
  onClick?: (event: React.MouseEvent) => void;
}

const ContentManageLink: React.FC<ContentManageLinkProps> = ({
  entityType,
  contentId,
  status,
  from,
  className,
  onClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const preservedRootFrom = useMemo(
    () => getManageNavigationRootFrom(location.state),
    [location.state],
  );

  const isLearnerDetailContext = useMemo(
    () =>
      /^\/coaching\/explore\/(courses|simulations|podcasts)\/[^/]+$/.test(
        location.pathname,
      ) ||
      /^\/coaching\/(courses|simulations|podcasts)\/[^/]+$/.test(
        location.pathname,
      ),
    [location.pathname],
  );

  const manageFrom = useMemo(() => {
    if (isLearnerDetailContext) {
      return location.pathname;
    }

    if (preservedRootFrom) {
      return preservedRootFrom;
    }

    const listOriginPaths = [
      COACHING_EXPLORE_PATH,
      COACHING_MY_COURSES_PATH,
      COACHING_MY_PODCASTS_PATH,
    ];

    if (from && !listOriginPaths.includes(from)) {
      return from;
    }

    if (location.pathname.startsWith(COACHING_EXPLORE_PATH)) {
      return COACHING_EXPLORE_PATH;
    }

    if (
      location.pathname.startsWith("/coaching/courses/") ||
      location.pathname.startsWith("/coaching/simulations/") ||
      location.pathname.startsWith(`${COACHING_MY_PODCASTS_PATH}/`)
    ) {
      return location.pathname;
    }

    const stateFrom = (location.state as { from?: string } | null)?.from;
    if (stateFrom && (isCoachingOriginPath(stateFrom) || stateFrom.includes("/reports"))) {
      return stateFrom;
    }

    if (from) {
      return from;
    }

    return COACHING_MY_COURSES_PATH;
  }, [from, isLearnerDetailContext, location.pathname, location.state, preservedRootFrom]);

  const manageRootFrom = useMemo(() => {
    const stateFrom = (location.state as { from?: string } | null)?.from;

    if (location.pathname.startsWith(COACHING_EXPLORE_PATH)) {
      return COACHING_EXPLORE_PATH;
    }

    if (stateFrom && isCoachingOriginPath(stateFrom)) {
      return stateFrom;
    }

    if (stateFrom?.startsWith(COACHING_EXPLORE_PATH)) {
      return COACHING_EXPLORE_PATH;
    }

    if (preservedRootFrom && isCoachingOriginPath(preservedRootFrom)) {
      return preservedRootFrom;
    }

    if (preservedRootFrom?.startsWith(COACHING_EXPLORE_PATH)) {
      return COACHING_EXPLORE_PATH;
    }

    if (preservedRootFrom) {
      return preservedRootFrom;
    }

    return manageFrom;
  }, [isLearnerDetailContext, location.pathname, location.state, manageFrom, preservedRootFrom]);

  const manageTarget = useMemo(() => {
    if (!contentId || !entityType) {
      return "";
    }

    if (
      entityType === "simulation" ||
      entityType === "course" ||
      entityType === "podcast"
    ) {
      const reportOnly = isReportOnlyCoachingUser();
      const isPublished =
        reportOnly ||
        isLearnerDetailContext ||
        isPublishedCatalogStatus(status);
      return getContentManageLink(entityType, contentId, {
        from: manageFrom,
        status: isPublished ? "PUBLISHED" : status,
        published: isPublished,
      });
    }

    return getContentManageLink(entityType, contentId, {
      from: manageFrom,
      status,
    });
  }, [contentId, entityType, isLearnerDetailContext, manageFrom, status]);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onClick?.(event);

      if (!manageTarget) {
        return;
      }

      navigateToManageTarget(navigate, manageTarget, {
        from: manageFrom,
        rootFrom: manageRootFrom,
        navigationState: location.state,
      });
    },
    [manageFrom, manageRootFrom, manageTarget, navigate, onClick, location.state],
  );

  const reportOnly = isReportOnlyCoachingUser();

  if (!canUseContentManageLink() || !manageTarget) {
    return null;
  }

  return (
    <div className={cx(styles.wrapper, className)}>
      <Actions.Button
        className={cx(styles.iconButton, styles.manage)}
        icon={reportOnly ? "paperflite-eye" : "paperflite-edit"}
        title={reportOnly ? "view reports" : "manage"}
        width={2}
        onClick={handleClick}
      />
    </div>
  );
};

export const useShowContentManageLink = (): boolean => {
  const location = useLocation();

  if (!canUseContentManageLink()) {
    return false;
  }

  const { pathname } = location;

  const isCourseContentsPath =
    pathname.startsWith("/coaching/courses/") && pathname.includes("/contents");
  const isExploreCourseContentsPath =
    pathname.startsWith("/coaching/explore/courses/") &&
    pathname.includes("/contents");

  if (isCourseContentsPath || isExploreCourseContentsPath) {
    return false;
  }

  return (
    pathname.startsWith("/coaching/explore/simulations/") ||
    pathname.startsWith("/coaching/explore/courses/") ||
    pathname.startsWith("/coaching/explore/podcasts/") ||
    pathname.startsWith("/coaching/simulations/") ||
    pathname.startsWith("/coaching/courses/") ||
    pathname.startsWith(COACHING_MY_PODCASTS_PATH)
  );
};

export { ContentManageLink };
